import type { ReactNode } from 'react'

import { ErrorDetails } from '@/components/ui/Alert'
import { Button } from '@/components/ui/Button'
import { cn } from '@/lib/cn'
import { presentError } from '@/lib/errors'

export interface ErrorStateProps {
  /** The failure itself. Presentation is derived from it, not hand-written. */
  error: unknown
  /** Used when the error is not one the app recognises. */
  fallbackTitle?: string
  onRetry?: () => void
  actions?: ReactNode
  /** `block` fills an empty region; `inline` sits above content that still renders. */
  variant?: 'block' | 'inline'
  className?: string
}

const WarningIcon = () => (
  <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" aria-hidden="true">
    <path
      d="M12 8.5v4.5M12 16.5v.01M10.3 3.9L2.6 17.4A2 2 0 0 0 4.3 20.4h15.4a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

/**
 * A region that could not load.
 *
 * One heading, one sentence, then the actions. The exception text sits behind
 * "Show technical details" — present for whoever needs to paste it into a bug
 * report, absent for everyone else.
 */
export function ErrorState({
  error,
  fallbackTitle,
  onRetry,
  actions,
  variant = 'block',
  className,
}: ErrorStateProps) {
  const presented = presentError(error, fallbackTitle)

  if (variant === 'inline') {
    return (
      <div
        role="alert"
        className={cn(
          'flex items-start gap-2.5 rounded-lg border border-critical/35 bg-critical-soft p-3',
          className,
        )}
      >
        <span className="mt-0.5 shrink-0 text-critical">
          <WarningIcon />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-base font-semibold text-ink">{presented.title}</p>
          <p className="mt-0.5 text-sm text-ink-secondary">{presented.message}</p>
          {presented.detail ? <ErrorDetails text={presented.detail} /> : null}
          {onRetry || actions ? (
            <div className="mt-2.5 flex flex-wrap items-center gap-2">
              {onRetry && presented.retryable ? (
                <Button size="sm" variant="secondary" onClick={onRetry}>
                  Try again
                </Button>
              ) : null}
              {actions}
            </div>
          ) : null}
        </div>
      </div>
    )
  }

  return (
    <div
      role="alert"
      className={cn('flex flex-col items-center px-6 py-14 text-center', className)}
    >
      <span className="mb-3 flex h-11 w-11 items-center justify-center rounded-full bg-critical-soft text-critical">
        <WarningIcon />
      </span>
      <h3 className="text-lg font-semibold text-ink">{presented.title}</h3>
      <p className="mt-1 max-w-md text-sm text-ink-secondary">{presented.message}</p>
      {onRetry || actions ? (
        <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
          {onRetry && presented.retryable ? (
            <Button variant="primary" onClick={onRetry}>
              Try again
            </Button>
          ) : null}
          {actions}
        </div>
      ) : null}
      {presented.detail ? (
        <div className="mt-3 w-full max-w-lg text-left">
          <ErrorDetails text={presented.detail} />
        </div>
      ) : null}
    </div>
  )
}
